import { useState } from 'react'
import { motion } from 'framer-motion'
import useStore from '../store'
import { CONFIG } from '../config/config'

export default function ReviewPage() {
  const goals          = useStore(s => s.goals)
  const reviews        = useStore(s => s.reviews)
  const reviewDue      = useStore(s => s.reviewDue)
  const submitReview   = useStore(s => s.submitReview)
  const has            = useStore(s => s.has)
  const consistency    = useStore(s => s.consistencyScore())
  const pva            = useStore(s => s.plannedVsActual())
  const accuracy       = useStore(s => s.estimationAccuracy())

  const active = goals.filter(g => g.status === 'active')

  const [hours, setHours] = useState(
    active.reduce((a, g) => ({ ...a, [g.id]: '' }), {})
  )
  const [notes,  setNotes]  = useState('')
  const [busy,   setBusy]   = useState(false)
  const [done,   setDone]   = useState(false)

  const submit = async () => {
    setBusy(true)
    const actualHours = Object.fromEntries(
      Object.entries(hours)
        .filter(([, v]) => v !== '')
        .map(([k, v]) => [k, Number(v)])
    )
    await submitReview({ actualHours, notes })
    setBusy(false)
    setDone(true)
  }

  if (done) {
    return (
      <div className="h-full flex items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <p className="font-mono text-2xl text-m-accent mb-2">Review complete.</p>
          <p className="font-mono text-xs text-m-faint">Next review in {CONFIG.REVIEW_CYCLE_DAYS} days.</p>
        </motion.div>
      </div>
    )
  }

  return (
    <motion.div
      className="h-full overflow-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
    >
      <div className="max-w-xl mx-auto px-5 py-5 flex flex-col gap-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-mono text-sm text-m-text">Weekly Review</h1>
            <p className="font-mono text-[10px] text-m-subtle mt-0.5">
              {reviewDue ? 'Review due' : 'Optional review'}
              {reviews.length > 0 && ` · ${reviews.length} logged`}
            </p>
          </div>
        </div>

        {/* Trend metrics */}
        {reviews.length >= CONFIG.MIN_REVIEWS_FOR_TREND && (
          <div className="card p-4 grid grid-cols-3 gap-4">
            <div>
              <p className="label">Consistency</p>
              <p className={`font-mono text-xl mt-1 ${
                (consistency ?? 0) >= 80 ? 'text-m-accent' :
                (consistency ?? 0) >= 60 ? 'text-m-warn'   : 'text-m-danger'
              }`}>
                {consistency !== null ? `${consistency}%` : '—'}
              </p>
            </div>
            {pva && (
              <>
                <div>
                  <p className="label">Planned</p>
                  <p className="font-mono text-xl mt-1 text-m-text">{pva.planned.toFixed(1)}h</p>
                </div>
                <div>
                  <p className="label">Actual</p>
                  <p className={`font-mono text-xl mt-1 ${pva.actual >= pva.planned * 0.8 ? 'text-m-accent' : 'text-m-warn'}`}>
                    {pva.actual.toFixed(1)}h
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Estimation accuracy (basic_analysis unlock) */}
        {has('basic_analysis') && accuracy && (
          <div className="card p-4">
            <p className="label mb-2">Estimation accuracy ({accuracy.count} phases)</p>
            <p className="font-mono text-sm text-m-text">
              You typically use{' '}
              <span className={`font-medium ${accuracy.ratio > 1.2 ? 'text-m-warn' : accuracy.ratio < 0.8 ? 'text-m-accent' : 'text-m-text'}`}>
                {(accuracy.ratio * 100).toFixed(0)}%
              </span>
              {' '}of estimated time.
              {accuracy.ratio > 1.2 && ' You tend to underestimate.'}
              {accuracy.ratio < 0.8 && ' You tend to overestimate.'}
              {accuracy.ratio >= 0.8 && accuracy.ratio <= 1.2 && ' Calibration is solid.'}
            </p>
          </div>
        )}

        {/* Log actual hours */}
        <div className="card p-4 flex flex-col gap-3">
          <p className="label">Hours logged this week</p>
          {active.length === 0 && (
            <p className="font-mono text-xs text-m-faint">No active goals.</p>
          )}
          {active.map(g => (
            <div key={g.id} className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
              <span className="font-mono text-xs text-m-text flex-1 truncate">{g.title}</span>
              {g.weekly_hours && (
                <span className="font-mono text-[10px] text-m-faint shrink-0">planned {g.weekly_hours}h</span>
              )}
              <input
                type="number" min="0" step="0.5"
                className="input-sm w-20 text-right"
                placeholder="0"
                value={hours[g.id] ?? ''}
                onChange={e => setHours(h => ({ ...h, [g.id]: e.target.value }))}
              />
            </div>
          ))}
        </div>

        {/* Notes */}
        <div className="card p-4">
          <p className="label mb-2">Notes</p>
          <textarea
            className="textarea"
            rows={3}
            placeholder="What happened this week? Any blockers or wins?"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>

        {/* History */}
        {reviews.length > 0 && (
          <div className="card p-4">
            <p className="label mb-3">History</p>
            <div className="flex flex-col gap-2">
              {reviews.slice(0, 8).map(r => {
                const total = Object.values(r.actual_hours ?? {}).reduce((s, h) => s + Number(h), 0)
                return (
                  <div key={r.id} className="flex items-center justify-between">
                    <span className="font-mono text-[11px] text-m-faint">{r.week_start}</span>
                    <span className="font-mono text-[11px] text-m-subtle">{total.toFixed(1)}h logged</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <button onClick={submit} disabled={busy} className="btn-primary w-full">
          {busy ? 'Saving…' : 'Complete Review'}
        </button>
      </div>
    </motion.div>
  )
}
